import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, UntypedFormBuilder } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { capitalizeFirstCharacterInWord } from '@app/shared/utils';

import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'add-category',
  templateUrl: './add-category.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCategoryComponent implements OnInit, OnDestroy {
  @ViewChild('categoryInput', { static: false }) categoryInput: ElementRef;
  @ViewChild('expenseIncomeCategoryDropdown') expenseIncomeCategory: MatSelect;
  @Input() transactionType;
  @Input() showType = false;
  @Input() fromRules = false;
  @Input() fromMobile = false;
  @Input() parentCategory = '';
  @Input() selectedCategories = [];
  @Input() selectedValueOption;
  @Input() selectedVendorOption;
  @Input() fromFloaterMenu = false;
  @Input() selectedValue: any[] = [];
  @Input() fromTransactionFilter = false;
  @Input() canCreateIncomeExpense = false;
  @Input() disableCategoryButton = false;
  @Output() changeCategoryEventEmitter = new EventEmitter();
  @Output() openEventEmitter = new EventEmitter();
  @Output() closeEventEmitter = new EventEmitter();

  searchText = '';
  allCategories = [];
  selectedCategory;
  categoryName = '';
  categoryFilterData;
  panelClass: string;
  loader = [1, 2, 3, 4];
  selectedCondition = '';
  selectedOptionsString: string = '';
  subscription = new Subscription();

  typeMap = {
    incomeGroup: ['income', 'refund of expense', 'transfer from'],
    expenseGroup: ['expense', 'refund of income', 'transfer to'],
  };

  constructor(private _fb: UntypedFormBuilder, private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.selectedValue?.length) this.selectedValue = this.selectedValue.filter((item) => item.value !== '');
    if (!this.selectedValue?.length) this.selectedValue.push({ level: 'type', value: 'All' });
    this.selectedOptionsString = this.selectedValue?.map((item) => capitalizeFirstCharacterInWord(item?.value ?? '')).join(', ') ?? '';
    this.cd.detectChanges();
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
    if (!this.fromFloaterMenu && !this.fromMobile && !this.fromRules && !this.fromTransactionFilter) {
      this.expenseIncomeCategory.open();
      this.subscription.add(
        this.expenseIncomeCategory.openedChange.subscribe((opened) => {
          if (opened) {
            setTimeout(() => {
              const panel = this.expenseIncomeCategory.panel.nativeElement as HTMLElement;
              panel.style.visibility = 'visible';
              this.cd.detectChanges();
            });
          }
        })
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCategories'] && changes['selectedCategories'].currentValue) {
      this.allCategories = changes['selectedCategories'].currentValue;
      this.selectedCategories = this.allCategories;
      this.updateCategoriesBasedOnType(this.transactionType);
    }
  }

  onCategorySelectionChange(event, subcategoryLevel = false) {
    if (subcategoryLevel) this.selectedValue = [event];
    if (Array.isArray(this.selectedValue)) {
      if (this.selectedValue.length) {
        this.selectedOptionsString = this.selectedValue.map((item) => capitalizeFirstCharacterInWord(item.value)).join(', ');
      }
    } else {
      this.selectedOptionsString = this.selectedValue;
    }

    this.categoryFilterData = typeof this.selectedValue !== 'string' ? this.constructCategoryHierarchy(this.selectedValue) : this.selectedValue;
  }

  filterCategoriesBySubCategory(searchInput: string, groupedData: any[]) {
    if (searchInput) {
      const lowerCaseSearch = searchInput.toLowerCase();
      return groupedData
        .map((group) => {
          const filteredCategories = group.categories
            .map((category) => {
              const filteredSubCategories = category.subCategories.filter((subCategory) => subCategory.name.toLowerCase().includes(lowerCaseSearch));
              if (filteredSubCategories.length > 0) {
                return {
                  ...category,
                  isOpen: true,
                  subCategories: filteredSubCategories,
                };
              }
              return null;
            })
            .filter((category) => category !== null);

          if (filteredCategories.length > 0) {
            return {
              ...group,
              isOpen: true,
              categories: filteredCategories,
            };
          }
          return null;
        })
        .filter((group) => group !== null);
    } else {
      return groupedData.map((group) => ({
        ...group,
        isOpen: false,
        categories: group.categories.map((category) => ({
          ...category,
          isOpen: false,
        })),
      }));
    }
  }

  constructCategoryHierarchy(filterData: any[]) {
    const result = {
      type: null,
      category: null,
      category_parent: null,
    };

    const types = new Set<string>();
    const categories = new Set<string>();
    const subcategories = new Set<string>();

    filterData.forEach((item) => {
      if (item.level === 'type') {
        types.add(item.value);
      } else if (item.level === 'category') {
        types.add(item.parent);
        categories.add(item.value);
      } else if (item.level === 'subcategory') {
        types.add(item.parent);
        categories.add(item.category);
        subcategories.add(item.value);
      }
    });

    result.type = types.size > 0 ? Array.from(types).join(', ') : null;
    result.category_parent = categories.size > 0 ? Array.from(categories).join(', ') : null;
    result.category = subcategories.size > 0 ? Array.from(subcategories).join(', ') : null;

    return result;
  }

  toggleIsOpenForCategory(type: string | null, index: number | null, groupedData: any[]) {
    return groupedData.map((group, idx) => ({
      ...group,
      categories: group.categories.map((category) => ({
        ...category,
        isOpen: false,
      })),
      isOpen: index !== null ? idx === index : group.type === type ? !group.isOpen : false,
    }));
  }

  toggleIsOpenForSubCategory(type: string, categoryIndex: number, groupedData: any[]) {
    return groupedData.map((group) => {
      if (group.type === type) {
        return {
          ...group,
          categories: group.categories.map((category, idx) => ({
            ...category,
            isOpen: idx === categoryIndex ? !category.isOpen : false,
          })),
        };
      }
      return group;
    });
  }

  openCategoryListForSelectedType(ev: Event, index, item) {
    ev.preventDefault();
    ev.stopPropagation();
    this.selectedCategories = this.toggleIsOpenForCategory(item.type, null, this.selectedCategories);
    this.cd.detectChanges();
  }

  openSubCategoryListForSelectedCategory(ev: Event, category, index) {
    ev.stopPropagation();
    ev.preventDefault();
    this.selectedCategories = this.toggleIsOpenForSubCategory(category.type, index, this.selectedCategories);
    this.cd.detectChanges();
  }

  onSearchChange() {
    this.selectedCategories = this.filterCategoriesBySubCategory(this.searchText, this.allCategories);
  }

  selectAllType() {
    this.selectedValue = [];
    this.selectedValue.push({ level: 'type', value: 'All' });
    this.selectedOptionsString = this.selectedValue[0].value;
    this.selectedCategories = this.allCategories;
    this.cd.detectChanges();
    this.categoryFilterData = {
      type: this.selectedOptionsString,
      category_parent: null,
      category: null,
    };
    this.expenseIncomeCategory.close();
  }

  updateCategoriesBasedOnType(type: string) {
    if (!type && this.fromRules) {
      this.selectedCondition = this.selectedVendorOption ? this.selectedVendorOption : '';
      this.getTransactionType('vendor');
    } else if (type && ['income', 'refund of expense', 'transfer from'].includes(type)) {
      this.selectedCategories = this.selectedCategories.filter((category) => this.typeMap['incomeGroup'].includes(category.type));
    } else if (type && ['expense', 'refund of income', 'transfer to'].includes(type)) {
      this.selectedCategories = this.selectedCategories.filter((category) => this.typeMap['expenseGroup'].includes(category.type));
    }
    this.allCategories = this.selectedCategories;
  }

  getTransactionType(type = '', option = '') {
    // if (type == 'vendor') {
    //   this.selectedVendorOption = option;
    //   this.selectedCondition = this.selectedVendorOption ? this.selectedVendorOption : this.selectedValueOption ? this.selectedValueOption : '';
    // }
    // if (type == 'value') {
    //   this.selectedValueOption = option;
    //   this.selectedCondition = this.selectedValueOption ? this.selectedValueOption : this.selectedVendorOption ? this.selectedVendorOption : '';
    // }
    // switch (this.selectedCondition) {
    //   case 'incomeAmount':
    //     this.selectedCategories = this.selectedCategories.filter((group) => group.type === 'income');
    //     break;
    //   case 'expenseAmount':
    //     this.selectedCategories = this.selectedCategories.filter((group) => group.type === 'expense');
    //     break;
    //   default:
    //     this.selectedCategories = this.selectedCategories.filter((group) => group.type === 'income' && group.type === 'expense');
    //     break;
    // }
    // this.cd.detectChanges();
  }

  openDropDownEvent(isOpen: boolean, event: MatSelect) {
    if (isOpen) {
      const viewportHeight = window.innerHeight;
      const selectRect = event._elementRef.nativeElement.getBoundingClientRect();
      const spaceBelow = viewportHeight - selectRect.bottom;
      const spaceAbove = selectRect.top;
      if (spaceBelow < 300 && spaceAbove > spaceBelow) {
        this.panelClass = 'mat-select-panel-above';
      } else {
        this.panelClass = 'mat-select-panel-below';
      }
      this.openEventEmitter.emit(isOpen);
    } else {
      this.searchText = '';
      this.changeCategoryEventEmitter.emit(this.categoryFilterData);
      this.categoryFilterData = null;
      this.closeEventEmitter.emit(true);
    }
    this.cd.detectChanges();
  }

  openCategoryDropdown() {
    this.expenseIncomeCategory.open();
  }

  closeCategoryDropdown() {
    this.expenseIncomeCategory.close();
  }

  selectAllContent(event) {
    event.target.select();
  }

  ngOnDestroy() {
    this.disableCategoryButton = false;
    this.subscription?.unsubscribe();
  }
}
